<template>
  <aside class="ev" aria-labelledby="titulo-proximos-eventos">
    <div class="ev-head">
      <span class="ev-head-icon">
        <span class="material-symbols-outlined">event</span>
      </span>
      <div>
        <p class="ev-eyebrow">Agenda</p>
        <h3 id="titulo-proximos-eventos" class="ev-title">Próximos eventos</h3>
      </div>
    </div>

    <ul v-if="loading" class="ev-list">
      <li v-for="n in 3" :key="n" class="ev-skeleton"></li>
    </ul>

    <p v-else-if="error" class="ev-empty">No se pudieron cargar los eventos.</p>

    <p v-else-if="!proximos.length" class="ev-empty">
      <span class="material-symbols-outlined">event_available</span>
      No hay eventos programados próximamente.
    </p>

    <ul v-else class="ev-list">
      <li v-for="evento in proximos" :key="evento.id">
        <button
          type="button"
          class="ev-item"
          aria-haspopup="dialog"
          :aria-label="`${evento.title}, ${longDate(evento.start)}. Ver detalles`"
          @click="abrir(evento)"
        >
          <span class="ev-date" :class="{ 'is-today': evento.esHoy }" :style="{ '--c': color(evento) }">
            <span class="ev-date-month">{{ meses[evento.start.getMonth()] }}</span>
            <span class="ev-date-day">{{ evento.start.getDate() }}</span>
          </span>
          <span class="ev-info">
            <span class="ev-item-title">{{ evento.title }}</span>
            <span class="ev-item-meta">
              <template v-if="evento.esHoy">Hoy</template>
              <template v-else>{{ dias[evento.start.getDay()] }}</template>
              <template v-if="!evento.allDay"> · {{ formatTime(evento.start) }}</template>
            </span>
            <span v-if="evento.description" class="ev-item-desc">{{ evento.description }}</span>
          </span>
          <span class="material-symbols-outlined ev-item-chevron">chevron_right</span>
        </button>
      </li>
    </ul>

    <router-link :to="{ name: 'Eventos' }" class="ev-cta">
      <span class="material-symbols-outlined">calendar_month</span>
      Ver calendario completo
    </router-link>

    <!-- Detalle del evento -->
    <b-modal
      id="evento-detalle"
      size="md"
      centered
      hide-footer
      modal-class="sd-modal"
      body-class="evd-body"
      @hidden="seleccionado = null"
    >
      <template #modal-header>
        <span class="evd-eyebrow">
          <span class="material-symbols-outlined">event</span> Evento
        </span>
        <button type="button" class="sd-modal-close" aria-label="Cerrar" @click="$bvModal.hide('evento-detalle')">
          <span class="material-symbols-outlined">close</span>
        </button>
      </template>

      <article v-if="seleccionado" class="evd" :style="{ '--c': color(seleccionado) }">
        <header class="evd-head">
          <span class="evd-date">
            <span class="evd-date-month">{{ meses[seleccionado.start.getMonth()] }}</span>
            <span class="evd-date-day">{{ seleccionado.start.getDate() }}</span>
          </span>
          <div class="evd-head-text">
            <span class="evd-tag">{{ categoria(seleccionado).label }}</span>
            <h2 class="evd-title">{{ seleccionado.title }}</h2>
          </div>
        </header>

        <ul class="evd-meta">
          <li>
            <span class="material-symbols-outlined">calendar_today</span>
            <span class="evd-meta-text">{{ fechaLarga(seleccionado.start) }}</span>
          </li>
          <li>
            <span class="material-symbols-outlined">schedule</span>
            <span class="evd-meta-text">{{ timeRange(seleccionado) }}</span>
          </li>
          <li v-if="seleccionado.location">
            <span class="material-symbols-outlined">location_on</span>
            <a :href="mapsUrl(seleccionado.location)" target="_blank" rel="noopener" class="evd-meta-text">
              {{ seleccionado.location }}
            </a>
          </li>
        </ul>

        <p v-if="seleccionado.description" class="evd-desc">{{ seleccionado.description }}</p>

        <footer class="evd-actions">
          <a
            v-track:anadir_calendario="{ evento: seleccionado.title, origen: 'portada' }"
            :href="googleCalendarLink(seleccionado)"
            target="_blank"
            rel="noopener"
            class="sd-btn"
          >
            <span class="material-symbols-outlined">event_available</span>
            Añadir a mi calendario
          </a>
          <router-link :to="{ name: 'Eventos', query: { fecha: toISODate(seleccionado.start) } }" class="sd-btn sd-btn--ghost">
            <span class="material-symbols-outlined">calendar_month</span>
            Ver en el calendario
          </router-link>
        </footer>

        <botones-compartir
          class="evd-share"
          etiqueta="Compartir"
          compacto
          :titulo="seleccionado.title"
          :texto="textoEvento(seleccionado)"
          :ruta="rutaEvento(seleccionado)"
          tipo="evento"
        />
      </article>
    </b-modal>
  </aside>
</template>

<script>
import axios from "@/directives/axios.js";
import { track } from "@/directives/analitica.js";
import BotonesCompartir from "@/components/compartir/BotonesCompartir.vue";
import {
  MONTH_SHORT,
  WEEKDAYS_LONG,
  normalizeEvent,
  getCategory,
  sameDay,
  formatTime,
  longDate,
  timeRange,
  googleCalendarLink,
  toISODate,
  rutaEvento,
  textoEvento,
} from "@/directives/eventos.js";

const MAX_EVENTOS = 4;

export default {
  name: "EventosCalendario",
  components: { BotonesCompartir },

  data() {
    return {
      eventos: [],
      loading: true,
      error: false,
      seleccionado: null,
      meses: MONTH_SHORT,
      dias: WEEKDAYS_LONG,
    };
  },

  computed: {
    proximos() {
      const ahora = new Date();
      const hoy = new Date(ahora.getFullYear(), ahora.getMonth(), ahora.getDate());
      return this.eventos
        .map(normalizeEvent)
        .filter((e) => e && (e.end || e.start) >= (e.allDay ? hoy : ahora))
        .sort((a, b) => a.start - b.start)
        .slice(0, MAX_EVENTOS)
        .map((e) => ({ ...e, esHoy: sameDay(e.start, hoy) }));
    },
  },

  mounted() {
    this.cargarEventos();
  },

  methods: {
    async cargarEventos() {
      try {
        const { data } = await axios.get("/eventos");
        if (data.success) {
          this.eventos = data.eventos || [];
        } else {
          this.error = true;
        }
      } catch (error) {
        console.error("Error cargando eventos:", error);
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    abrir(evento) {
      track("ver_evento", {
        evento: evento.title,
        categoria: evento.category,
        fecha: toISODate(evento.start),
        origen: "portada",
      });
      this.seleccionado = evento;
      this.$nextTick(() => this.$bvModal.show("evento-detalle"));
    },
    categoria(evento) {
      return getCategory(evento.category);
    },
    color(evento) {
      return getCategory(evento.category).color;
    },
    fechaLarga(d) {
      const texto = longDate(d);
      return texto.charAt(0).toUpperCase() + texto.slice(1);
    },
    mapsUrl(lugar) {
      return "https://www.google.com/maps/search/?api=1&query=" + encodeURIComponent(lugar);
    },
    formatTime,
    longDate,
    timeRange,
    googleCalendarLink,
    toISODate,
    rutaEvento,
    textoEvento,
  },
};
</script>

<style scoped>
.ev {
  position: sticky;
  top: 120px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 26px;
  border: 1px solid var(--sd-border);
  border-radius: 24px;
  background: var(--sd-surface);
  box-shadow: var(--sd-shadow);
  text-align: left;
  color: var(--sd-text);
}

.ev-head {
  display: flex;
  align-items: center;
  gap: 14px;
}

.ev-head-icon {
  display: grid;
  place-items: center;
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: var(--sd-gold-soft);
}

.ev-head-icon .material-symbols-outlined {
  font-size: 24px;
  color: var(--sd-gold);
}

.ev-eyebrow {
  margin: 0;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--sd-gold);
}

.ev-title {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--sd-text);
}

.ev-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.ev-item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: calc(100% + 20px);
  padding: 10px;
  margin: 0 -10px;
  border: 0;
  border-radius: 14px;
  background: transparent;
  font: inherit;
  color: var(--sd-text);
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

.ev-item:hover {
  background: var(--sd-surface-2);
}

.ev-item-chevron {
  flex-shrink: 0;
  margin-left: auto;
  font-size: 22px;
  color: var(--sd-muted);
  opacity: 0;
  transform: translateX(-4px);
  transition: opacity 0.15s, transform 0.15s;
}

.ev-item:hover .ev-item-chevron,
.ev-item:focus-visible .ev-item-chevron {
  opacity: 1;
  transform: none;
}

@media (hover: none) {
  .ev-item-chevron {
    opacity: 1;
    transform: none;
  }
}

.ev-item:focus-visible {
  outline: 2px solid var(--sd-primary);
  outline-offset: 2px;
}

.ev-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 52px;
  height: 56px;
  border: 1px solid var(--sd-border);
  border-radius: 14px;
  background: var(--sd-surface-2);
  line-height: 1.1;
}

.ev-date-month {
  font-size: 0.66rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--sd-primary);
}

.ev-date-day {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--sd-text);
}

.ev-date.is-today {
  border-color: var(--sd-primary);
  background: var(--sd-primary);
}

.ev-date.is-today .ev-date-month,
.ev-date.is-today .ev-date-day {
  color: var(--sd-primary-contrast);
}

.ev-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  padding-top: 2px;
}

.ev-item-title {
  overflow: hidden;
  font-weight: 700;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: var(--sd-text);
}

.ev-item-meta {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--sd-primary);
}

.ev-item-desc {
  display: -webkit-box;
  overflow: hidden;
  font-size: 0.83rem;
  line-height: 1.4;
  color: var(--sd-muted);
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.ev-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 0;
  padding: 24px 12px;
  text-align: center;
  color: var(--sd-muted);
}

.ev-empty .material-symbols-outlined {
  font-size: 32px;
  color: var(--sd-muted);
}

.ev-skeleton {
  height: 64px;
  border-radius: 14px;
  background: linear-gradient(90deg, var(--sd-surface-2) 25%, var(--sd-surface) 50%, var(--sd-surface-2) 75%);
  background-size: 200% 100%;
  animation: ev-shimmer 1.4s infinite linear;
}

@keyframes ev-shimmer {
  from {
    background-position: 200% 0;
  }
  to {
    background-position: -200% 0;
  }
}

.ev-cta {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 46px;
  border-radius: 12px;
  background: var(--sd-primary);
  font-weight: 600;
  color: var(--sd-primary-contrast);
  text-decoration: none;
  transition: background 0.15s;
}

.ev-cta:hover {
  background: var(--sd-primary-strong);
  color: var(--sd-primary-contrast);
  text-decoration: none;
}

.ev-cta .material-symbols-outlined {
  font-size: 20px;
  color: inherit;
}

.ev-date {
  border-top: 3px solid var(--c, var(--sd-border));
}

@media (max-width: 1099px) {
  .ev {
    position: static;
  }
}

/* ---------- Modal de detalle ---------- */
.evd-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--sd-gold);
}

.evd-eyebrow .material-symbols-outlined {
  font-size: 18px;
  color: inherit;
}

.evd {
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: left;
  color: var(--sd-text);
}

.evd-head {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.evd-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 64px;
  height: 70px;
  border-radius: 16px;
  background: var(--c);
  line-height: 1.1;
  box-shadow: 0 6px 16px color-mix(in srgb, var(--c) 35%, transparent);
}

.evd-date-month {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.9);
}

.evd-date-day {
  font-size: 1.7rem;
  font-weight: 700;
  color: #fff;
}

.evd-head-text {
  min-width: 0;
}

.evd-tag {
  display: inline-block;
  margin-bottom: 6px;
  padding: 2px 10px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--c) 18%, transparent);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--sd-text);
}

.evd-title {
  margin: 0;
  font-size: 1.45rem;
  font-weight: 700;
  line-height: 1.25;
  color: var(--sd-text);
  overflow-wrap: anywhere;
}

.evd-meta {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 16px;
  border: 1px solid var(--sd-border);
  border-radius: 14px;
  background: var(--sd-surface-2);
  list-style: none;
}

.evd-meta li {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.evd-meta .material-symbols-outlined {
  flex-shrink: 0;
  font-size: 20px;
  color: var(--c);
}

.evd-meta-text {
  color: var(--sd-text);
  overflow-wrap: anywhere;
}

a.evd-meta-text {
  text-decoration: underline;
  text-underline-offset: 2px;
}

a.evd-meta-text:hover {
  color: var(--sd-primary);
}

.evd-desc {
  margin: 0;
  font-size: 0.98rem;
  line-height: 1.65;
  white-space: pre-line;
  color: var(--sd-text);
  overflow-wrap: anywhere;
}

.evd-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 4px;
}

.evd-share {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid var(--sd-border);
}

@media (max-width: 480px) {
  .evd-actions .sd-btn {
    flex: 1 1 100%;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ev-skeleton {
    animation: none;
  }
}
</style>
